import { useUserSession } from '@solinkcorp/authentication';
import {
  type SdmLivePlayerErrorCode,
  type SdmLivePlayerProps,
  type SdmLivePlayerState,
} from '@solinkcorp/service-display-monitor';
import {
  callhomeCameraToCameraIdentifier,
  type PlayingCamera,
} from 'components/VideoPlayer/controller/PlayingCamerasController';
import { usePlaybackController } from 'components/VideoPlayer/controller/usePlaybackController';
import React, { useEffect, useMemo, useRef } from 'react';
import { useControllerState } from 'solox';
import CameraPlayer from 'src/core/video/components/players/CameraPlayer';
import { useDeviceIsLocal } from 'src/hooks/useDeviceIsLocal';
import { useIsNetworkOnline } from 'src/hooks/useIsNetworkOnline';
import { type VideoDiscoverySite } from 'utils/types';
import { toSdmLivePlayerErrorCode, toSdmLivePlayerState } from './sdmLivePlayerState';
import { usePlayerModules } from './usePlayerModules';

/**
 * Plays a camera live for the Service Display Monitor's live feed widget.
 *
 * This is the SDM package's `LiveVideoProvider` implementation: SDM owns the layout, rotation and
 * error overlay, and delegates the stream itself to the app's `CameraPlayer` (WebRTC when the
 * device supports it, HLS otherwise). Unlike `QuickPlayer` there is no seeking, thumbnail or
 * overlay work — the feed is always live.
 */
const SdmLiveFeedPlayer: React.FC<SdmLivePlayerProps> = ({
  camera,
  device,
  site,
  onStateChange,
  onStreamChange,
  onVideoElement,
}) => {
  const user = useUserSession();
  const locationIsLocal = useDeviceIsLocal(camera.deviceId);

  // The SDM package reads sites straight from CallHome, so build the shape the playback stack
  // expects rather than going through the web app's LocationStore, which the standalone SDM app
  // never populates.
  const location = useMemo<VideoDiscoverySite>(() => {
    const { cameras, ...siteWithoutCameras } = site;
    const base = {
      ...siteWithoutCameras,
      lastUpdateTime: new Date(),
      primaryDeviceId: device.id,
    };
    return { ...base, cameras: cameras.map((c) => ({ ...c, location: base })) };
  }, [site, device.id]);

  const playingCameras: PlayingCamera[] = useMemo(
    () => [{ ...callhomeCameraToCameraIdentifier(camera), camera, location, device }],
    [camera, location, device],
  );
  const playingCamera = playingCameras[0];

  // Starting at the current time puts the controller straight into live playback.
  const controller = usePlaybackController(playingCameras, 'sd', Date.now, { quick: true });

  const playerState = useControllerState(
    controller.state,
    (state) => state.byPlayerId[playingCamera.playerId]?.status?.state,
  );
  const playerError = useControllerState(
    controller.state,
    (state) => state.byPlayerId[playingCamera.playerId]?.status?.error,
  );
  const streamId = useControllerState(
    controller.state,
    (state) => state.byPlayerId[playingCamera.playerId]?.state.streamInfo?.streamId ?? null,
  );

  const isOnline = useIsNetworkOnline();
  const playerModules = usePlayerModules(isOnline);
  const reportedRef = useRef<SdmLivePlayerState>('connecting');
  const reportedErrorCodeRef = useRef<SdmLivePlayerErrorCode | undefined>(undefined);

  useEffect(() => {
    reportedRef.current = toSdmLivePlayerState({
      previous: reportedRef.current,
      playerState: playerModules === 'failed' ? 'error' : playerState,
      isOnline,
    });
    reportedErrorCodeRef.current = toSdmLivePlayerErrorCode({
      state: reportedRef.current,
      previous: reportedErrorCodeRef.current,
      playerState,
      playerError,
      isOnline,
    });
    onStateChange?.(reportedRef.current, reportedErrorCodeRef.current);
  }, [playerState, playerModules, playerError, isOnline, onStateChange]);

  useEffect(() => {
    onStreamChange?.(streamId);
  }, [streamId, onStreamChange]);

  const wasOnlineRef = useRef(isOnline);
  useEffect(() => {
    if (isOnline && !wasOnlineRef.current) {
      controller.restartPlayer(playingCamera.playerId, 'Network back online');
    }
    wasOnlineRef.current = isOnline;
  }, [isOnline, controller, playingCamera.playerId]);

  if (playerModules !== 'loaded') return null;

  return (
    <CameraPlayer
      playbackController={controller}
      playingCamera={playingCamera}
      locationIsLocal={locationIsLocal || false}
      user={user}
      setVideoRef={onVideoElement}
      isMuted
    />
  );
};

export default React.memo(SdmLiveFeedPlayer);
